// Respuesta de GET /dashboards/:dashboardId/widgets/:widgetId/map-data (widget de mapa).

/** ok = envia datos; stale = mas de 24 h sin enviar; never = sin datos en la ventana de busqueda. */
export type MapSensorStatus = 'ok' | 'stale' | 'never';

/** Color del punto: ok = todos sus sensores envian; partial = unos si y otros no (revisar); off = ninguno. */
export type MapDeviceStatus = 'ok' | 'partial' | 'off';

export interface MapSensor {
  id:       string;
  position: number;
  type:     number;
  lastDate: string | null;
  status:   MapSensorStatus;
  /** Todas las variables de la ultima lectura; la principal se elige con el catalogo de sensores. */
  values:   Record<string, number | string>;
}

/** Una variable elegida por el admin (una regla del widget): una casilla de la leyenda. */
export interface MapVariable {
  /** id de la regla, o "manual:<variable>" si solo la usan manuales */
  id:         string;
  /** De donde sale: una regla del admin ("Dispositivos") o solo manuales ("Manual") */
  source:     'DEVICES' | 'MANUAL';
  fieldName:  string;
  sensorType: number | null;
  label:      string | null;
  unit:       string | null;
  color:      string | null;
}

/** Valor de una variable elegida en un dispositivo (ultima lectura de su sensor). */
export interface MapVariableValue {
  variableId: string;
  sensorId:   string;
  position:   number;
  /** null = sin lectura (no es un cero) */
  value:      number | string | null;
  lastDate:   string | null;
  status:     MapSensorStatus;
}

export interface MapDevice {
  id:           string;
  /** Con variables elegidas: los valores de esas variables (vacio si no hay variables). */
  values:       MapVariableValue[];
  /** Vinculacion del dueño del area con el dispositivo: ruta /intranet/devices/:userDeviceId. */
  userDeviceId: number;
  serialNumber: string;
  name:         string | null;
  lat:          number;
  long:         number;
  /** Viene de una entrada manual del widget y no del area. */
  isManual:     boolean;
  /** Bateria del equipo principal en mV (null si no se conoce). */
  vBus:         number | null;
  lastDate:     string | null;
  status:       MapDeviceStatus;
  sensors:      MapSensor[];
}

// Colores y nombre comunes al widget de mapa y a su tarjeta "Entradas" (un solo sitio).
const MAP_GREEN = '#03b862';
const MAP_ORANGE = '#f5a300';
const MAP_GREY = '#92949c';
/** Sensor: envia (verde) o no envia (gris: mas de 24 h o nunca, para el usuario es lo mismo). */
export const MAP_SENSOR_COLORS: Record<MapSensorStatus, string> = { ok: MAP_GREEN, stale: MAP_GREY, never: MAP_GREY };
/** Dispositivo: todos envian (verde), unos si y otros no = revisar (naranja), ninguno (gris). */
export const MAP_DEVICE_COLORS: Record<MapDeviceStatus, string> = { ok: MAP_GREEN, partial: MAP_ORANGE, off: MAP_GREY };

/** "Nombre · SERIE", o solo la serie si no tiene nombre. */
export function mapDeviceName(device: Pick<MapDevice, 'name' | 'serialNumber'>): string {
  return device.name ? `${device.name} · ${device.serialNumber}` : device.serialNumber;
}

export interface MapPolygon {
  areaEntryId: string;
  name:        string;
  /** Nombre de la propiedad Polígono (el que le puso el admin): la leyenda. */
  propertyLabel?: string | null;
  /** Regla de polígono: el color de la entrada de esta área (el del 🎨); sin él, el azul de siempre. */
  color?: string | null;
  /** Regla de polígono que la pinta (sus áreas van juntas en la leyenda). */
  groupId?: string | null;
  /** Dispositivos del área (la etiqueta al pasar el ratón). */
  deviceCount?: number;
  /** Áreas que la contienen, de la más cercana a la raíz. */
  parentNames?: string[];
  /** Dashboard de esa área, si lo tiene (el botón "Abrir dashboard" de su panel). */
  dashboardId?: string | null;
  /** Puede abrirse aunque la copia del usuario todavia no se haya creado. */
  hasDashboard?: boolean;
  /** Nombre de la plantilla del área (el que le puso el admin: "Parcela", "Sector"). */
  templateName?: string | null;
  /** GeoJSON: [anillo][punto][lng, lat]. */
  coordinates: number[][][];
}

export interface WidgetMapData {
  /** false en el dashboard de la plantilla: no hay un area real que mostrar. */
  hasInstance:            boolean;
  /** Variables elegidas por el admin. Vacio = todos los dispositivos con su variable principal. */
  variables:              MapVariable[];
  /** El admin eligio variables: solo salen los dispositivos que las tienen (y los manuales). */
  filtersByVariables:     boolean;
  polygon:                MapPolygon | null;
  /** El mapa tiene la entrada del contorno pero el área no tiene el polígono dibujado. */
  polygonMissing:         boolean;
  /** El contorno no apunta al Polígono de esta área (área no encontrada o propiedad de otra plantilla). */
  polygonMisconfigured:   boolean;
  /** Plantilla del admin (sin área real): los polígonos configurados (nombre de la propiedad), para la leyenda. */
  polygonLegend?: Array<{ id: string; label: string }>;
  /** Para abrir el dashboard de un área al pinchar su polígono: la instancia y el área de este dashboard. */
  areaInstanceDocumentId?: string | null;
  ownAreaEntryId?:         string | null;
  /** Otros Polígonos del área del dashboard (contornos con otra propiedad Polígono): se dibujan como `polygon`. */
  otherOwnPolygons?:      MapPolygon[];
  subareaPolygons:        MapPolygon[];
  /** Polígonos de otras áreas del mismo árbol añadidos a mano (sin repetir los ya pintados). */
  manualPolygons:         MapPolygon[];
  devices:                MapDevice[];
  /** Igual que `devices` (sensores, estado, ultimo dato...) pero sin coordenadas. */
  devicesWithoutLocation: Array<Omit<MapDevice, 'lat' | 'long'>>;
}
